import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RefreshCw, Lock, CircleHelp } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsSyncButton } from '../../components/options/OptionsSyncButton';
import { DeribitApiTutorialModal } from '../../components/options/DeribitApiTutorialModal';
import { connectOptionsManually, disconnectOptionsAccount, fetchOptionsOverview, syncOptionsAccount } from '../../lib/optionsApi';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import GeniLoader from '../../components/GeniLoader';
import { formatDateTime } from '../../components/options/optionsHistoryUtils';
import { getOptionsErrorMessage } from '../../lib/optionsErrorUtils';

const DERIBIT_PERMISSIONS_ERROR_CODE = 'deribit_api_permissions_incompatible';

export default function DeribitConnect() {
  const { t, isPortuguese } = useOptionsLocale();
  const navigate = useNavigate();
  const { isDemoMode, exitDemoMode } = useOptionsMode();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [environment, setEnvironment] = useState('production');
  const [flowStep, setFlowStep] = useState('idle');
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [permissionsIncompatible, setPermissionsIncompatible] = useState(false);
  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [initialSyncError, setInitialSyncError] = useState('');

  const connectionState = loadingOverview ? 'loading' : (overview?.connected ? 'connected' : 'disconnected');
  const isConnecting = flowStep === 'connecting';
  const isInitialSyncing = flowStep === 'syncing';
  const isProcessingConnectionFlow = isConnecting || isInitialSyncing;

  const goToHome = () => navigate('/options');
  const isConnected = connectionState === 'connected';
  const environmentLabel = overview?.environment === 'testnet' ? t('common.testnet') : t('common.production');
  const lastSyncLabel = overview?.last_sync_at ? formatDateTime(overview.last_sync_at, isPortuguese ? 'pt' : 'en') : t('connection.notSyncedYet');
  const overviewLoadingTitle = isPortuguese ? 'Carregando conexão Deribit…' : 'Loading Deribit connection…';
  const overviewLoadingDescription = isPortuguese ? 'Mantendo o shell GENI enquanto o estado da conta é resolvido.' : 'Keeping the GENI shell visible while account state is resolved.';
  const connectLoadingTitle = isConnecting ? t('connection.connecting') : t('connection.syncingData');
  const connectLoadingDescription = isConnecting
    ? t('connection.connectingDescription')
    : t('connection.syncingDescription');

  const loadOverview = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoadingOverview(true);
    }

    try {
      const data = await fetchOptionsOverview();
      setOverview(data);
      setEnvironment(data?.environment === 'testnet' ? 'testnet' : 'production');
      if (data?.sync_status !== 'error') {
        setInitialSyncError('');
      }
      return data;
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('toasts.failedLoadConnect'), isPortuguese));
      return null;
    } finally {
      if (!silent) {
        setLoadingOverview(false);
      }
    }
  }, [isPortuguese, t]);

  useEffect(() => {
    let active = true;
    const loadConnectState = async () => {
      if (isDemoMode) {
        setLoadingOverview(false);
        return;
      }

      const data = await loadOverview();
      if (!active) {
        return;
      }

      setOverview(data);
    };

    loadConnectState();
    return () => {
      active = false;
    };
  }, [isDemoMode, loadOverview]);

  const runManualSync = useCallback(async ({ redirectOnSuccess = false, retryingInitialSync = false } = {}) => {
    setInitialSyncError('');
    setSyncing(true);

    try {
      await syncOptionsAccount();
      await loadOverview({ silent: true });
      toast.success(t('toasts.syncCompleted'));

      if (redirectOnSuccess) {
        navigate('/options', { replace: true });
      }
    } catch (error) {
      const syncMessage = getOptionsErrorMessage(error, t('toasts.syncFailed'), isPortuguese);
      await loadOverview({ silent: true });
      toast.error(syncMessage);

      if (retryingInitialSync) {
        setInitialSyncError(syncMessage);
      }
    } finally {
      setSyncing(false);
    }
  }, [isPortuguese, loadOverview, navigate, t]);

  const handleManualConnect = async (event) => {
    event.preventDefault();
    setInitialSyncError('');
    setPermissionsIncompatible(false);
    setFlowStep('connecting');

    try {
      await connectOptionsManually({ client_id: clientId, client_secret: clientSecret, environment });
      setFlowStep('syncing');
      setClientId('');
      setClientSecret('');
      await runManualSync({ redirectOnSuccess: true, retryingInitialSync: true });
    } catch (error) {
      if (error?.response?.data?.detail === DERIBIT_PERMISSIONS_ERROR_CODE) {
        setPermissionsIncompatible(true);
      } else {
        toast.error(getOptionsErrorMessage(error, t('toasts.connectionFailed'), isPortuguese));
      }
    } finally {
      setFlowStep('idle');
    }
  };

  const handleSync = async () => {
    await runManualSync();
  };

  const handleRetryInitialSync = async () => {
    await runManualSync({ redirectOnSuccess: true, retryingInitialSync: true });
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectOptionsAccount();
      await loadOverview({ silent: true });
      setShowDisconnectConfirm(false);
      toast.success(t('connection.disconnectedToast'));
    } catch (error) {
      toast.error(getOptionsErrorMessage(error, t('connection.disconnectFailedToast'), isPortuguese));
    } finally {
      setDisconnecting(false);
    }
  };

  const pageTitle = connectionState === 'loading'
    ? t('common.manageConnection')
    : connectionState === 'connected'
      ? t('connection.manageDeribit')
      : t('common.connectDeribit');
  const pageDescription = connectionState === 'disconnected' ? t('performance.connectDescription') : '';
  const pageEyebrow = connectionState === 'connected' ? t('connection.connectedEyebrow') : t('connection.connectEyebrow');
  const headerActions = !loadingOverview ? (
    <Button asChild type="button" variant="outline" className="rounded-full border-white/10 bg-white/[0.03] px-4 text-white/80 hover:bg-white/8 hover:text-white" data-testid="options-connect-back-dashboard-button">
      <Link to="/options">{t('common.backToDashboard')}</Link>
    </Button>
  ) : null;

  return (
    <OptionsPageShell title={pageTitle} description={pageDescription} eyebrow={pageEyebrow} actions={headerActions}>
      {loadingOverview ? (
        <OptionsLoadingState
          title={overviewLoadingTitle}
          description={overviewLoadingDescription}
          testId="options-connect-loading-state"
        />
      ) : isDemoMode ? (
        <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-connect-demo-read-only-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold font-heading" data-testid="options-connect-demo-read-only-title">{t('performance.demoReadonlyTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-white/65" data-testid="options-connect-demo-read-only-description">
              {t('performance.demoReadonlyDescription')}
            </p>
            <Button
              type="button"
              variant="outline"
              className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10 hover:text-white"
              onClick={() => {
                exitDemoMode();
                goToHome();
              }}
              data-testid="options-connect-demo-exit-button"
            >
              {t('common.exitDemo')}
            </Button>
          </CardContent>
        </Card>
      ) : overview?.access_mode === 'admin_shared_testnet' ? (
        <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-connect-admin-managed-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold font-heading" data-testid="options-connect-admin-managed-title">{t('performance.adminManagedTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-white/65" data-testid="options-connect-admin-managed-description">
              {t('performance.adminManagedDescription')}
            </p>
            <Button type="button" onClick={goToHome} data-testid="options-connect-admin-managed-dashboard-button">
              {t('common.backToDashboard')}
            </Button>
          </CardContent>
        </Card>
      ) : isConnected ? (
        <div className="grid gap-4" data-testid="options-connect-connected-grid">
          <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-connect-connected-card">
            <CardHeader className="space-y-4">
              <div className="flex flex-wrap items-center gap-2" data-testid="options-connect-connected-badges">
                <Badge className="border-[#22C55E]/20 bg-[#22C55E]/12 text-[#D8FBE1]" data-testid="options-connect-status-badge">● {t('connection.connectedStatus')}</Badge>
                <Badge className={overview?.environment === 'testnet' ? 'border-[#F0B90B]/25 bg-[#F0B90B]/12 text-[#F7D47A]' : 'border-emerald-400/20 bg-emerald-500/12 text-emerald-200'} data-testid="options-connect-environment-badge">{environmentLabel}</Badge>
                <Badge className="border-white/10 bg-white/[0.05] text-white/75" data-testid="options-connect-readonly-badge">{t('connection.readOnlyActive')}</Badge>
              </div>
              <div className="space-y-2">
                <CardTitle className="text-lg font-bold font-heading" data-testid="options-connect-connected-title">{t('connection.manageDeribit')}</CardTitle>
                <p className="text-sm text-white/60" data-testid="options-connect-connected-description">{t('connection.connectedDescription')}</p>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="options-connect-connected-metrics">
                <InfoPill label={t('connection.statusLabel')} value={t('connection.connectedStatus')} testId="options-connect-status-value" />
                <InfoPill label={t('connection.environmentLabel')} value={environmentLabel} testId="options-connect-environment-value" />
                <InfoPill label={t('connection.lastSyncLabel')} value={lastSyncLabel} testId="options-connect-last-sync-value" />
                <InfoPill label={t('connection.permissionsLabel')} value={overview?.permissions?.length ? overview.permissions.join(' · ') : t('connection.readOnlyActive')} testId="options-connect-permissions-value" />
              </div>

              <div className="flex flex-wrap items-center gap-3" data-testid="options-connect-connected-actions">
                {initialSyncError || overview?.sync_status === 'error' ? (
                  <div className="w-full rounded-3xl border border-amber-400/20 bg-amber-400/8 px-4 py-4 text-left" data-testid="options-connect-sync-warning">
                    <p className="text-sm font-semibold text-[#F7D47A]" data-testid="options-connect-sync-warning-title">
                      {t('connection.initialSyncFailed')}
                    </p>
                    <p className="mt-2 text-sm text-white/70" data-testid="options-connect-sync-warning-description">
                      {initialSyncError || overview?.sync_error || t('toasts.syncFailed')}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="options-connect-sync-warning-actions">
                      <Button
                        type="button"
                        onClick={handleRetryInitialSync}
                        disabled={syncing}
                        className="gap-2"
                        data-testid="options-connect-retry-initial-sync-button"
                      >
                        {syncing ? t('connection.syncingData') : t('connection.retryInitialSync')}
                      </Button>
                    </div>
                  </div>
                ) : null}
                {overview?.can_sync !== false ? <OptionsSyncButton onSync={handleSync} syncing={syncing} testId="options-connect-sync-button" /> : null}
                {overview?.can_manage_connection !== false ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-transparent text-white/78 hover:bg-white/5 hover:text-white"
                    onClick={() => setShowDisconnectConfirm(true)}
                    data-testid="options-connect-disconnect-button"
                  >
                    {t('connection.disconnectDeribit')}
                  </Button>
                ) : (
                  <Button type="button" variant="outline" className="border-white/10 bg-transparent text-white/70 hover:bg-white/5 hover:text-white" onClick={goToHome} data-testid="options-connect-connected-dashboard-button">
                    {t('common.backToDashboard')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
            <AlertDialogContent className="border-white/10 bg-[#121212] text-white" data-testid="options-connect-disconnect-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-white" data-testid="options-connect-disconnect-dialog-title">{t('connection.disconnectTitle')}</AlertDialogTitle>
                <AlertDialogDescription className="space-y-3 text-white/65" data-testid="options-connect-disconnect-dialog-description">
                  <p>{t('connection.disconnectDescription')}</p>
                  <p className="text-[#F7D47A]">{t('connection.disconnectWarningCurrentBehavior')}</p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-white/10 bg-transparent text-white hover:bg-white/5 hover:text-white" data-testid="options-connect-disconnect-cancel-button">
                  {t('connection.cancelDisconnect')}
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-[#8B1E2D] text-white hover:bg-[#9E2335]"
                  onClick={(event) => {
                    event.preventDefault();
                    handleDisconnect();
                  }}
                  disabled={disconnecting}
                  data-testid="options-connect-disconnect-confirm-button"
                >
                  {disconnecting ? t('connection.disconnecting') : t('connection.confirmDisconnect')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="options-connect-grid">
          <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-connect-manual-card">
            <CardHeader>
              <CardTitle className="text-lg font-bold font-heading" data-testid="options-connect-manual-title">{t('common.connectDeribit')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-5" onSubmit={handleManualConnect} data-testid="options-connect-manual-form" aria-busy={isProcessingConnectionFlow}>
                <p className="text-sm text-white/60" data-testid="options-connect-manual-description">{t('performance.connectDescription')}</p>
                <div className="space-y-2" data-testid="options-connect-environment-group">
                  <Label data-testid="options-connect-environment-label">{t('common.environment')}</Label>
                  <div className="flex flex-wrap gap-2" data-testid="options-connect-environment-options">
                    <Button
                      type="button"
                      variant="outline"
                      className={environment === 'production' ? 'border-emerald-400/40 bg-emerald-500/15 text-white hover:bg-emerald-500/20 hover:text-white' : 'border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/10 hover:text-white'}
                      onClick={() => setEnvironment('production')}
                      disabled={isProcessingConnectionFlow}
                      data-testid="options-connect-environment-production-button"
                    >
                      {t('common.production')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className={environment === 'testnet' ? 'border-[#F0B90B]/35 bg-[#F0B90B]/12 text-[#F7D47A] hover:bg-[#F0B90B]/18 hover:text-[#F7D47A]' : 'border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/10 hover:text-white'}
                      onClick={() => setEnvironment('testnet')}
                      disabled={isProcessingConnectionFlow}
                      data-testid="options-connect-environment-testnet-button"
                    >
                      {t('common.testnet')}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="options-client-id" data-testid="options-connect-client-id-label">Client ID</Label>
                  <Input
                    id="options-client-id"
                    value={clientId}
                    onChange={(event) => setClientId(event.target.value)}
                    placeholder="e.g. ABC123"
                    disabled={isProcessingConnectionFlow}
                    data-testid="options-connect-client-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="options-client-secret" data-testid="options-connect-client-secret-label">Client Secret</Label>
                  <Input
                    id="options-client-secret"
                    type="password"
                    value={clientSecret}
                    onChange={(event) => setClientSecret(event.target.value)}
                    placeholder="••••••••"
                    disabled={isProcessingConnectionFlow}
                    data-testid="options-connect-client-secret-input"
                  />
                </div>
                <p className="text-xs uppercase tracking-[0.22em] text-white/45" data-testid="options-connect-helper">
                  {t('onboarding.securityTitle')} {t('onboarding.securityDetails')}
                </p>
                {isProcessingConnectionFlow ? (
                  <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-4 py-4" data-testid="options-connect-inline-processing-state">
                    <div className="flex items-start gap-3">
                      <div className="rounded-full border border-white/10 bg-black/20 p-2" data-testid="options-connect-inline-processing-icon">
                        <GeniLoader size="sm" inline className="py-0.5" />
                      </div>
                      <div className="space-y-1 text-left" data-testid="options-connect-inline-processing-copy">
                        <p className="text-sm font-semibold text-white" data-testid="options-connect-inline-processing-title">{connectLoadingTitle}</p>
                        <p className="text-sm text-white/60" data-testid="options-connect-inline-processing-description">{connectLoadingDescription}</p>
                      </div>
                    </div>
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={isProcessingConnectionFlow}
                  className={
                    isProcessingConnectionFlow
                      ? 'gap-2 border border-white/10 bg-white/[0.06] text-white/80 shadow-none hover:bg-white/[0.06] hover:text-white/80 disabled:opacity-100 disabled:bg-white/[0.06] disabled:text-white/80 disabled:border-white/10'
                      : 'gap-2'
                  }
                  data-testid="options-connect-submit-button"
                >
                  {isProcessingConnectionFlow ? (
                    <>
                      <RefreshCw size={16} className="animate-spin shrink-0" data-testid="options-connect-submit-button-spinner" />
                      <span data-testid="options-connect-submit-button-label">{connectLoadingTitle}</span>
                    </>
                  ) : t('common.connectDeribit')}
                </Button>

                {permissionsIncompatible ? (
                  <div className="rounded-2xl border border-[#FF8A8A]/25 bg-[#FF8A8A]/10 px-4 py-4" data-testid="options-connect-permissions-error">
                    <p className="text-sm font-bold text-[#FFB3B3]" data-testid="options-connect-permissions-error-title">{t('deribitTutorial.permissionsErrorTitle')}</p>
                    <p className="mt-1 text-sm text-white/70" data-testid="options-connect-permissions-error-body">{t('deribitTutorial.permissionsErrorBody')}</p>
                    <button
                      type="button"
                      onClick={() => setShowTutorial(true)}
                      className="mt-3 text-sm font-semibold text-white underline underline-offset-2 hover:text-[#00E676]"
                      data-testid="options-connect-permissions-error-view-tutorial"
                    >
                      {t('deribitTutorial.viewTutorialButton')}
                    </button>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={() => setShowTutorial(true)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#7FB8FF] transition-colors hover:text-[#A6CDFF]"
                  data-testid="options-connect-help-link"
                >
                  <CircleHelp size={14} />
                  {t('deribitTutorial.helpLinkLabel')}
                </button>

                <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3" data-testid="options-connect-security-block">
                  <div className="flex items-center gap-2 text-white/85">
                    <Lock size={14} />
                    <p className="text-xs font-bold uppercase tracking-[0.14em]" data-testid="options-connect-security-block-title">{t('deribitTutorial.securityBadgeTitle')}</p>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55" data-testid="options-connect-security-block-scopes">
                    <span>{t('deribitTutorial.scopeAccountLabel')}: {t('deribitTutorial.scopeReadValue')}</span>
                    <span>{t('deribitTutorial.scopeTradeLabel')}: {t('deribitTutorial.scopeReadValue')}</span>
                    <span>{t('deribitTutorial.scopeWalletLabel')}: {t('deribitTutorial.scopeReadValue')}</span>
                  </div>
                  <p className="mt-2 text-xs text-white/45" data-testid="options-connect-security-block-note">{t('deribitTutorial.cannotTradeNote')}</p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <DeribitApiTutorialModal open={showTutorial} onOpenChange={setShowTutorial} />
    </OptionsPageShell>
  );
}

const InfoPill = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4" data-testid={testId}>
    <p className="text-[11px] uppercase tracking-[0.18em] text-white/40">{label}</p>
    <p className="mt-2 text-sm font-semibold text-white" data-testid={`${testId}-value`}>{value}</p>
  </div>
);